/**
 * Face detection in the browser with MediaPipe Tasks Vision (BlazeFace short-range model).
 *
 * The IIFE bundle (global `Vision`), the wasm runtime and the model are fetched once, on first use,
 * so pages that never create an album automatically pay nothing. Paths come from the settings :
 *  - curatorFaceBundleUrl : /app/js/vendors/mediapipe/vision_bundle.js
 *  - curatorFaceWasmPath  : directory holding vision_wasm_internal.js / .wasm (CDN by default)
 *  - curatorFaceModelUrl  : /app/js/vendors/mediapipe/models/blaze_face_short_range.tflite
 *
 * detect() answers faces in normalized coordinates (0..1), the format models.Photo already uses.
 */
fx.ns("curator", class FaceFinder {

    constructor(options)
    {
        options = options || {};

        this.bundleUrl = options.bundleUrl;
        this.wasmPath = options.wasmPath;
        this.modelUrl = options.modelUrl;
        this.minConfidence = options.minConfidence || 0.6;
        this.minSuppression = options.minSuppression || 0.25;   // non-max suppression : lower merges more overlapping boxes
        this.minArea = (options.minArea !== undefined) ? options.minArea : 0.005;   // normalized width * height
        this.checkKeypoints = (options.checkKeypoints !== undefined) ? options.checkKeypoints : true;

        this._detector = null;
        this._loading = null;
        this._failed = false;
    }

    get supported()
    {
        return typeof WebAssembly == "object" && typeof document != "undefined" && !!this.bundleUrl && !!this.wasmPath && !!this.modelUrl
            && curator.FaceFinder.modernSyntax();
    }

    /**
     * The MediaPipe bundle and its wasm runtime use the logical assignments (`??=`, `||=`, `&&=`,
     * ES2021 : Chrome 85, Firefox 79, Safari 14) : an older browser throws a SyntaxError when the
     * script is parsed ("expected expression, got '='" on Firefox 78). Tested once, without loading anything.
     */
    static modernSyntax()
    {
        if (curator.FaceFinder._modernSyntax === undefined)
        {
            try
            {
                new Function("var a = null, b = 0, c = 1; a ??= 1; b ||= 1; c &&= 2;");
                curator.FaceFinder._modernSyntax = true;
            }
            catch (e)
            {
                curator.FaceFinder._modernSyntax = false;
            }
        }

        return curator.FaceFinder._modernSyntax;
    }

    /** True once loading failed : callers skip detection instead of retrying on every photo. */
    get failed()
    {
        return this._failed;
    }

    get ready()
    {
        return !!this._detector;
    }

    load()
    {
        if (this._detector)
            return Promise.resolve(this._detector);

        if (this._loading)
            return this._loading;

        if (!this.supported)
            return Promise.reject(new Error("face-detection-unsupported"));

        // the wasm runtime binds console.error when it is instantiated : the filter of its
        // informative lines must be in place during the initialisation (see _installFilter)
        let restore = this._installFilter();

        this._loading = this._loadBundle()
            .then(() => window.Vision.FilesetResolver.forVisionTasks(this.wasmPath))
            .then((fileset) => window.Vision.FaceDetector.createFromOptions(fileset, {
                baseOptions : { modelAssetPath : this.modelUrl, delegate : "CPU" },
                runningMode : "IMAGE",
                minDetectionConfidence : this.minConfidence,
                minSuppressionThreshold : this.minSuppression
            }))
            .then((detector) => {
                restore();
                this._detector = detector;
                this._loading = null;
                return detector;
            })
            .catch((e) => {
                restore();
                this._failed = true;
                this._loading = null;
                throw e;
            });

        return this._loading;
    }

    /**
     * Resolves with the faces of a canvas, biggest first : [{ x, y, width, height, score }], normalized.
     */
    detect(canvas)
    {
        return this.load().then((detector) => {
            let result = detector.detect(canvas);
            let detections = (result && result.detections) ? result.detections : [];
            let faces = [];

            for (let i = 0; i < detections.length; i++)
            {
                let box = detections[i].boundingBox;
                if (!box || !box.width || !box.height)
                    continue;

                let categories = detections[i].categories;
                let score = (categories && categories.length) ? Number(categories[0].score) : 0;

                let x = Math.max(0, Math.min(1, box.originX / canvas.width));
                let y = Math.max(0, Math.min(1, box.originY / canvas.height));
                let width = Math.max(0, Math.min(1 - x, box.width / canvas.width));
                let height = Math.max(0, Math.min(1 - y, box.height / canvas.height));

                if (!(width > 0 && height > 0) || width * height < this.minArea)
                    continue;

                if (this.checkKeypoints && !this._plausible(detections[i].keypoints, x, y, width, height))
                    continue;

                faces.push({ x : x, y : y, width : width, height : height, score : score });
            }

            return faces.sort((a, b) => b.width * b.height - a.width * a.height);
        });
    }

    dispose()
    {
        if (this._detector)
        {
            try { this._detector.close(); } catch (e) {}
            this._detector = null;
        }
    }

    // ------------------------------------------------------------------ //

    /**
     * BlazeFace answers six keypoints (right eye, left eye, nose, mouth, right ear, left ear),
     * normalized to the image. A real face has both eyes above the mouth, and the eyes apart by at
     * least a fifth of the box width (measured 0.30 to 0.35 on real faces, the box includes the hair) :
     * textures, logos and shop windows fail these two checks.
     */
    _plausible(keypoints, x, y, width, height)
    {
        if (!keypoints || keypoints.length < 4)
            return true; // nothing to check against

        let rightEye = keypoints[0], leftEye = keypoints[1], mouth = keypoints[3];
        if (!rightEye || !leftEye || !mouth)
            return true;

        if (rightEye.y >= mouth.y || leftEye.y >= mouth.y)
            return false;

        let eyesApart = Math.abs(leftEye.x - rightEye.x);
        if (eyesApart < width * 0.2)
            return false;

        // the mouth sits inside the box
        if (mouth.x < x || mouth.x > x + width || mouth.y < y || mouth.y > y + height)
            return false;

        return true;
    }

    /**
     * The wasm runtime writes informative lines ("INFO: Created TensorFlow Lite XNNPACK delegate
     * for CPU.") on its stderr, which Emscripten relays through the console.error it captured at
     * instantiation. The graph's own lines come in glog format instead, a level letter, the date
     * and the time ("W1021 09:43:07.191000 2196592 gl_context.cc:1119] OpenGL error checking is
     * disabled") : info and warning ones are dropped too, errors ("E…") still go through.
     * Wrapping the console while the module is created gives the runtime a filtered outlet for
     * good ; the originals are put back right after, for everybody else.
     * Answers the function restoring the originals.
     */
    _installFilter()
    {
        let original = { error : console.error, warn : console.warn, log : console.log };
        let quiet = (name) => function () {
            let first = arguments[0];
            if (typeof first == "string" && /^\s*((INFO|WARNING):|[IW]\d{4} \d\d:\d\d:\d\d)/.test(first))
                return;
            return original[name].apply(console, arguments);
        };

        console.error = quiet("error");
        console.warn = quiet("warn");
        console.log = quiet("log");

        return () => {
            console.error = original.error;
            console.warn = original.warn;
            console.log = original.log;
        };
    }

    /**
     * The bundle is fetched first, retried on a network failure, and only then added as a script,
     * which the browser takes from its cache. A <script> that fails to load raises a resource error
     * FlexiTrack reports ("Failed to load script …vision_bundle.js") although detection is only a
     * bonus : on a flaky connection, the failure now stays a rejected Promise, handled by the caller.
     */
    _loadBundle()
    {
        if (window.Vision && window.Vision.FaceDetector)
            return Promise.resolve();

        return this._prefetch(this.bundleUrl, 3).then(() => this._appendScript());
    }

    _prefetch(url, attempts)
    {
        if (typeof fetch != "function")
            return Promise.resolve();

        return fetch(url, { credentials : "same-origin" })
            .then((response) => {
                if (!response.ok)
                    throw new Error("vision-bundle-load " + response.status);
                return response.text();
            })
            .catch((e) => {
                if (attempts <= 1 || /vision-bundle-load 4\d\d/.test(e.message))
                    throw e;
                return new Promise((resolve) => setTimeout(resolve, 2000)).then(() => this._prefetch(url, attempts - 1));
            });
    }

    _appendScript()
    {
        return new Promise((resolve, reject) => {
            let script = document.createElement("script");
            script.src = this.bundleUrl;
            script.async = true;
            script.onload = () => (window.Vision && window.Vision.FaceDetector) ? resolve() : reject(new Error("vision-bundle-invalid"));
            script.onerror = () => reject(new Error("vision-bundle-load"));
            document.head.appendChild(script);
        });
    }
});
